import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    NgZone,
    afterNextRender,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';

import { AssetCard } from '@features/assets/components/asset-card/asset-card';
import {
    DisplayFormat,
    IAssetListItem,
    ListSize,
} from '@features/assets/components/asset-card/asset-list-item.types';

/**
 * Ideal (design) tile width per format/size, mirroring the fixed dimensions in
 * asset-card.scss. Used only to pick the column count in fluid mode — the actual
 * rendered width is `1fr` of the row, so tiles land within ~15% of these values.
 */
export const TILE_WIDTH: Record<DisplayFormat, Record<ListSize, number>> = {
    landscape: { xsmall: 220, small: 293, medium: 360, large: 580 },
    portrait: { xsmall: 220, small: 293, medium: 360, large: 450 },
    square: { xsmall: 220, small: 293, medium: 360, large: 450 },
    circle: { xsmall: 220, small: 293, medium: 360, large: 450 },
    radio: { xsmall: 200, small: 200, medium: 200, large: 200 },
};

/** Aspect ratio (`aspect-ratio` value) per format — matches the fixed card dimensions. */
export const TILE_ASPECT: Record<DisplayFormat, string> = {
    landscape: '16 / 9',
    portrait: '9 / 16',
    square: '1 / 1',
    circle: '1 / 1',
    radio: '1 / 1',
};

/** Column gap in px — matches the legacy per-card `margin-right`. Kept in sync with the SCSS. */
export const GRID_GAP = 20;

/**
 * Column count that keeps tiles closest to their design width while filling the row.
 * `round` (not `floor`) picks the nearest fit, so tiles land slightly under or over
 * the design width rather than bloating up to ~2× the way a min-width grid would.
 */
export function fluidColumnCount(width: number, format: DisplayFormat, size: ListSize): number {
    const ideal = TILE_WIDTH[format]?.[size] ?? 293;
    return Math.max(1, Math.round((width + GRID_GAP) / (ideal + GRID_GAP)));
}

@Component({
    selector: 'asset-grid',
    standalone: true,
    imports: [AssetCard],
    templateUrl: './asset-grid.html',
    styleUrl: './asset-grid.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        // Column count drives `repeat(var(--cols), 1fr)` in the SCSS (fluid mode only).
        '[style.--cols]': 'cols()',
    },
})
export class AssetGrid {
    private readonly host = inject(ElementRef<HTMLElement>);
    private readonly zone = inject(NgZone);
    private readonly destroyRef = inject(DestroyRef);

    readonly items = input.required<readonly IAssetListItem[]>();
    readonly displayFormat = input<DisplayFormat>('landscape');
    readonly size = input<ListSize>('small');
    readonly topChart = input(false);
    /**
     * Fluid tiles that resize to fill the row width edge-to-edge (Netflix-style),
     * keeping the aspect ratio and staying close to the design size. On by default
     * for every grid; opt out with `[fluid]="false"` to keep the old fixed sizes.
     */
    readonly fluid = input(true);
    /** data-testid stamped on every card in this grid (QA e2e hook). */
    readonly cardTestId = input<string>('rail_card');

    readonly itemClick = output<IAssetListItem>();

    /** The grid's current content width in px; fed by the ResizeObserver. */
    private readonly width = signal(0);

    /** Column count for the fluid grid (null in fixed mode); see `fluidColumnCount`. */
    protected readonly cols = computed(() => {
        const w = this.width();
        if (!this.fluid() || w <= 0) return null;
        return fluidColumnCount(w, this.displayFormat(), this.size());
    });

    constructor() {
        // Track the grid's own width. Runs outside Angular; the signal write is what
        // schedules re-render, so we hop back into the zone to keep CD predictable.
        afterNextRender(() => {
            const el = this.host.nativeElement;
            this.width.set(Math.round(el.clientWidth));
            const observer = new ResizeObserver(entries => {
                const w = entries[0]?.contentRect.width ?? el.clientWidth;
                this.zone.run(() => this.width.set(Math.round(w)));
            });
            observer.observe(el);
            this.destroyRef.onDestroy(() => observer.disconnect());
        });
    }

    onItemClick(item: IAssetListItem): void {
        this.itemClick.emit(item);
    }
}
